import { useApp } from "@/App";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Calendar, CheckCircle, Clock, AlertTriangle, RefreshCw, X, Users, Calculator } from "lucide-react";
import { format, parseISO } from "date-fns";
import { useSearchParams } from "react-router-dom";
import { useState } from "react";
import ShotsCalculator from "./ShotsCalculator";

const CompactFixture = ({ fixture, result, pendingEntry, onOpenCalc }) => {
  const isCompleted = fixture.is_completed && result && result.status === 'confirmed';
  const hasConflict = fixture.has_conflict || (result && result.status === 'conflict');
  const isPending = pendingEntry && !fixture.is_completed;

  return (
    <div 
      className={`py-2.5 px-3 border-b border-border last:border-b-0 ${
        hasConflict ? 'bg-red-50' : isPending ? 'bg-amber-50' : ''
      }`}
      data-testid={`fixture-row-${fixture.id}`}
    >
      <div className="flex items-center gap-2">
        {/* Date */}
        <div className="w-14 text-xs text-muted-foreground shrink-0">
          {fixture.match_date ? format(parseISO(fixture.match_date), "d MMM") : "TBC"}
        </div>

        {/* Teams & Score - inline */}
        <div className="flex-1 min-w-0 flex items-center gap-1 text-sm">
          <span className={`truncate ${isCompleted && result.home_points > result.away_points ? 'font-bold text-primary' : 'font-medium'}`}>
            {fixture.home_club_name}
          </span>
          {isCompleted ? (
            <span className="font-bold shrink-0">{result.home_score} - {result.away_score}</span>
          ) : (
            <span className="text-muted-foreground shrink-0">v</span>
          )}
          <span className={`truncate ${isCompleted && result.away_points > result.home_points ? 'font-bold text-primary' : 'font-medium'}`}>
            {fixture.away_club_name}
          </span>
        </div>

        {/* Status badge + Calculator */}
        <div className="shrink-0 flex items-center gap-1.5">
          <button
            onClick={(e) => { e.stopPropagation(); onOpenCalc(fixture); }}
            className="text-muted-foreground hover:text-primary transition-colors"
            title="Shots Calculator"
            data-testid={`shots-calc-${fixture.id}`}
          >
            <Calculator size={14} />
          </button>
          {hasConflict ? (
            <span className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-medium">CONFLICT</span>
          ) : isCompleted ? (
            <CheckCircle size={14} className="text-green-600" />
          ) : isPending ? (
            <Clock size={14} className="text-amber-600" />
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default function FixturesPage() {
  const { fixtures, results, pendingResults, seasons, clubs, selectedSeason, setSelectedSeason, loading, refreshData, lastRefresh } = useApp();
  const [searchParams, setSearchParams] = useSearchParams();
  const [calcFixture, setCalcFixture] = useState(null);
  
  const selectedClubId = searchParams.get('club') || '';
  const selectedClub = clubs.find(c => c.id === selectedClubId);

  const setClubFilter = (clubId) => {
    if (clubId && clubId !== 'all') {
      setSearchParams({ club: clubId });
    } else {
      setSearchParams({});
    }
  };

  const clearClubFilter = () => setSearchParams({});

  // Quick lookup maps
  const resultsMap = {};
  results.forEach(r => { resultsMap[r.fixture_id] = r; });
  const pendingMap = {};
  pendingResults.forEach(p => { if (!pendingMap[p.fixture_id]) pendingMap[p.fixture_id] = p; });

  // Filter by club
  const filteredFixtures = selectedClubId 
    ? fixtures.filter(f => f.home_club_id === selectedClubId || f.away_club_id === selectedClubId)
    : fixtures;

  // Categorise
  const conflicts = filteredFixtures.filter(f => f.has_conflict);
  const confirmed = filteredFixtures.filter(f => f.is_completed && !f.has_conflict && resultsMap[f.id]?.status === 'confirmed');
  const pending = filteredFixtures.filter(f => !f.is_completed && pendingMap[f.id]);
  const upcoming = filteredFixtures.filter(f => !f.is_completed && !pendingMap[f.id]);

  // Group upcoming by month
  const groupByMonth = (fixtureList) => {
    const groups = {};
    const sorted = [...fixtureList].sort((a, b) => {
      if (!a.match_date) return 1;
      if (!b.match_date) return -1;
      return new Date(a.match_date) - new Date(b.match_date);
    });
    sorted.forEach(f => {
      let monthKey = "Date TBC";
      if (f.match_date) {
        try { monthKey = format(parseISO(f.match_date), "MMMM yyyy"); } catch {}
      }
      if (!groups[monthKey]) groups[monthKey] = [];
      groups[monthKey].push(f);
    });
    return groups;
  };

  const upcomingGrouped = groupByMonth(upcoming);
  const confirmedGrouped = groupByMonth(confirmed);

  const conflictCount = conflicts.length;
  const confirmedCount = confirmed.length;
  const pendingCount = pending.length;
  const upcomingCount = upcoming.length;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="fixtures-page">
      {/* Header */}
      <div className="bg-primary px-4 pt-8 pb-5">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-extrabold text-white tracking-tight">
              {selectedClub ? selectedClub.name : "Fixtures & Results"}
            </h1>
          </div>
          <Button variant="ghost" size="icon" onClick={refreshData} className="text-white hover:bg-white/20" data-testid="refresh-btn">
            <RefreshCw size={20} />
          </Button>
        </div>

        {/* Filters */}
        <div className="flex gap-2 mt-4">
          {seasons.length > 0 && (
            <Select value={selectedSeason?.id || ""} onValueChange={(id) => setSelectedSeason(seasons.find(s => s.id === id))}>
              <SelectTrigger className="flex-1 bg-white/10 border-white/20 text-white" data-testid="fixtures-season-selector">
                <SelectValue placeholder="Select Season" />
              </SelectTrigger>
              <SelectContent>
                {seasons.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name} {s.is_active && "(Current)"}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Select value={selectedClubId || 'all'} onValueChange={setClubFilter}>
            <SelectTrigger className="flex-1 bg-white/10 border-white/20 text-white" data-testid="club-filter">
              <Users size={16} className="mr-2" />
              <SelectValue placeholder="All Clubs" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Clubs</SelectItem>
              {[...clubs].sort((a, b) => a.name.localeCompare(b.name)).map((club) => (
                <SelectItem key={club.id} value={club.id}>{club.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {selectedClub && (
          <div className="mt-3">
            <button onClick={clearClubFilter} className="inline-flex items-center gap-2 bg-[#CCFF00] text-primary px-3 py-1.5 rounded-full text-sm font-medium" data-testid="clear-club-filter">
              <Users size={14} /> {selectedClub.name} <X size={14} />
            </button>
          </div>
        )}

        {/* Stats */}
        {filteredFixtures.length > 0 && (
          <div className="flex flex-wrap gap-3 mt-3 text-sm">
            {conflictCount > 0 && <span className="text-red-300 flex items-center gap-1"><AlertTriangle size={13} />{conflictCount}</span>}
            <span className="text-white/80 flex items-center gap-1"><CheckCircle size={13} className="text-[#CCFF00]" />{confirmedCount}</span>
            {pendingCount > 0 && <span className="text-amber-300 flex items-center gap-1"><Clock size={13} />{pendingCount}</span>}
            <span className="text-white/80 flex items-center gap-1"><Calendar size={13} />{upcomingCount}</span>
          </div>
        )}
      </div>

      {/* Fixtures List */}
      <div className="px-4 py-3 pb-20 space-y-4">
        {loading ? (
          [...Array(6)].map((_, i) => <Skeleton key={i} className="h-16 w-full rounded-lg" />)
        ) : filteredFixtures.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground" data-testid="no-fixtures">
            <Calendar size={48} className="mx-auto mb-3 opacity-50" />
            <p className="text-lg">{selectedClub ? `No fixtures for ${selectedClub.name}` : "No fixtures available"}</p>
            {selectedClub && <Button variant="outline" className="mt-4" onClick={clearClubFilter}>View All Fixtures</Button>}
          </div>
        ) : (
          <>
            {/* Conflicts */}
            {conflictCount > 0 && (
              <div>
                <h2 className="text-xs font-bold text-red-600 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                  <AlertTriangle size={12} /> Conflicts ({conflictCount})
                </h2>
                <div className="bg-white rounded-lg border border-red-200 overflow-hidden">
                  {conflicts.map(f => (
                    <CompactFixture key={f.id} fixture={f} result={resultsMap[f.id]} pendingEntry={null} onOpenCalc={setCalcFixture} />
                  ))}
                </div>
              </div>
            )}

            {/* Confirmed grouped by month */}
            {confirmedCount > 0 && (
              <div>
                <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">Results ({confirmedCount})</h2>
                {Object.entries(confirmedGrouped).map(([month, fixes]) => (
                  <div key={month} className="mb-3">
                    <p className="text-xs font-semibold text-primary/70 mb-1 pl-1">{month}</p>
                    <div className="bg-white rounded-lg border border-border overflow-hidden">
                      {fixes.map(f => <CompactFixture key={f.id} fixture={f} result={resultsMap[f.id]} pendingEntry={null} onOpenCalc={setCalcFixture} />)}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Pending */}
            {pendingCount > 0 && (
              <div>
                <h2 className="text-xs font-bold text-amber-600 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                  <Clock size={12} /> Awaiting 2nd Entry ({pendingCount})
                </h2>
                <div className="bg-white rounded-lg border border-amber-200 overflow-hidden">
                  {pending.map(f => <CompactFixture key={f.id} fixture={f} result={null} pendingEntry={pendingMap[f.id]} onOpenCalc={setCalcFixture} />)}
                </div>
              </div>
            )}

            {/* Upcoming grouped by month */}
            {upcomingCount > 0 && (
              <div>
                <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">Upcoming ({upcomingCount})</h2>
                {Object.entries(upcomingGrouped).map(([month, fixes]) => (
                  <div key={month} className="mb-3">
                    <p className="text-xs font-semibold text-primary/70 mb-1 pl-1">{month}</p>
                    <div className="bg-white rounded-lg border border-border overflow-hidden">
                      {fixes.map(f => <CompactFixture key={f.id} fixture={f} result={null} pendingEntry={null} onOpenCalc={setCalcFixture} />)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        <p className="text-xs text-muted-foreground text-center pt-2">
          Last updated: {new Date(lastRefresh).toLocaleTimeString()}
        </p>
      </div>

      <ShotsCalculator 
        fixture={calcFixture} 
        open={!!calcFixture} 
        onClose={() => setCalcFixture(null)} 
      />
    </div>
  );
}
